/**
 * terrain_seat — footprint-aware seating on the procedural terrain (wave: grounding).
 *
 * Two float causes, both fixed here:
 *   1. A single-point sample floats objects on slopes (the downhill half of a
 *      canopy hangs in the air) → seat on the MIN across the footprint.
 *   2. The ANALYTIC height floats objects mid-cell: the rendered mesh linearly
 *      interpolates a 5.3–10.7 m vertex grid, sagging below the analytic field
 *      on curved steep ground → sample `meshHeight` (the exact rendered
 *      surface), not the field.
 * A slope-proportional extra embed sinks the base a little deeper on steep
 * ground (reads as talus), covering the residual within-footprint sag between
 * the 5 samples. `slope` (rise/run) lets callers skip cliff faces entirely.
 */
import { meshHeight } from './terrain_stamp.js';
// (mesh-interpolated + stamped: seats agree with the VISIBLE ground exactly)

export interface TerrainSeat {
  /** Y to place the object base at (min footprint mesh height − embed). */
  y: number;
  /** Approximate rise/run across the footprint (1.0 = 45°). */
  slope: number;
}

export function terrainSeat(
  seed: string, x: number, z: number, r: number, embed = 0.15,
  /** The ground sampled (default: the rendered mesh). A deed passes `meshHeightUnfit` — THE ROOM FIT changes the
   *  ground around a building, never where a building is seated (interiors programme C2, 2026-10-01). */
  sample: (seed: string, x: number, z: number) => number = meshHeight,
): TerrainSeat {
  const c = sample(seed, x, z);
  const a = sample(seed, x + r, z);
  const b = sample(seed, x - r, z);
  const d = sample(seed, x, z + r);
  const e = sample(seed, x, z - r);
  const min = Math.min(c, a, b, d, e);
  const max = Math.max(c, a, b, d, e);
  const slope = (max - min) / (2 * r);
  // Steeper ground → sink deeper (capped): hides the sag between samples.
  return { y: min - embed - Math.min(0.5, slope * 0.35), slope };
}
